import { addressLabel, addressMeta } from './listing-address-label';

/**
 * Shared lookup for creation, drafts, editing and invitations (address, parcel),
 * the admin developer filter and the place field of the map's start dialog.
 * `optionClass` and `messageClass` let a caller outside the listing form use the
 * shared menu items (`ms-menu__item`); `arrange(items, query)` orders or trims
 * the filtered items as a whole (the start dialog ranks exact names first and
 * drops duplicates). Enter picks the first option of an open list; pressed
 * while the list still shows its loading message, it picks the first option
 * once those results arrive, unless the text changes or the list closes first.
 * With `enterSubmits` Enter submits the form while no list is open (a search
 * form that also works without choosing a suggestion).
 */
export function initListingAutocomplete({ input, results, url, params, label, meta, onSelect, filter = () => true, arrange = items => items, minLength = 3, loading = 'Szukam adresu…', empty = 'Nie znaleziono adresu. Podaj ulicę i miejscowość.', error = 'Nie udało się wyszukać adresu. Spróbuj ponownie.', optionClass = '', messageClass = 'plf-address__empty', enterSubmits = false }) {
    if (!input || !results) return;
    let timer, controller, revision = 0;
    // The revision whose loading message the list shows, and the revision of
    // an Enter pressed meanwhile (null for none).
    let loadingRevision = null, enterRevision = null;
    const wrap = results.parentElement;
    // A listbox may hold only options, so searching, no result and errors are
    // drawn in the list for the eye and read from one live region beside it.
    const live = document.createElement('span');
    live.className = 'sr-only';
    live.setAttribute('role', 'status');
    wrap.append(live);
    const close = () => {
        ++revision;
        loadingRevision = null;
        enterRevision = null;
        clearTimeout(timer);
        controller?.abort();
        results.hidden = true;
        input.setAttribute('aria-expanded', 'false');
    };
    const show = () => { results.hidden = false; input.setAttribute('aria-expanded', 'true'); live.textContent = ''; };
    const message = text => {
        const node = document.createElement('div');
        node.className = messageClass;
        node.setAttribute('aria-hidden', 'true');
        node.textContent = text;
        results.replaceChildren(node);
        results.hidden = false;
        input.setAttribute('aria-expanded', 'false');
        live.textContent = text;
    };
    input.addEventListener('input', () => {
        close();
        results.replaceChildren();
        const query = input.value.trim();
        if (query.length < minLength) return;
        const current = revision;
        message(loading);
        loadingRevision = current;
        timer = setTimeout(async () => {
            controller = new AbortController();
            try {
                const endpoint = new URL(url, window.location.origin);
                Object.entries(params(query)).forEach(([key, value]) => endpoint.searchParams.set(key, value));
                const response = await fetch(endpoint, { signal: controller.signal, credentials: 'same-origin' });
                if (!response.ok) throw new Error();
                const data = await response.json();
                if (current !== revision) return;
                // The results replace the loading message; an Enter pressed over it picks their first option.
                const pick = enterRevision === current;
                loadingRevision = enterRevision = null;
                const items = arrange((Array.isArray(data.items) ? data.items : []).filter(filter), query);
                results.replaceChildren();
                if (!items.length) { message(empty); return; }
                items.forEach(item => {
                    const button = document.createElement('button');
                    button.type = 'button';
                    if (optionClass) button.className = optionClass;
                    button.setAttribute('role', 'option');
                    const title = document.createElement('strong');
                    title.textContent = label(item);
                    const detail = document.createElement('span');
                    detail.textContent = meta(item);
                    button.append(title, detail);
                    button.addEventListener('click', () => { close(); onSelect(item); input.focus(); });
                    results.append(button);
                });
                show();
                if (pick) results.querySelector('button').click();
            } catch (failure) {
                if (failure.name !== 'AbortError' && current === revision) {
                    loadingRevision = enterRevision = null;
                    message(error);
                }
            }
        }, 280);
    });
    input.addEventListener('keydown', event => {
        // Escape closes an open list and keeps the text: a search field would
        // otherwise clear itself by default.
        if (event.key === 'Escape') { if (!results.hidden) event.preventDefault(); close(); }
        if (event.key === 'ArrowDown' && !results.hidden) { event.preventDefault(); results.querySelector('button')?.focus(); }
        if (event.key === 'Enter') {
            const option = results.hidden ? null : results.querySelector('button');
            if (option || !enterSubmits) event.preventDefault();
            // A submitted search stops the pending suggestions.
            if (option) option.click();
            else if (enterSubmits) close();
            // Pressed while the suggestions load: the first one, once they arrive.
            else if (loadingRevision === revision) enterRevision = revision;
        }
    });
    results.addEventListener('keydown', event => {
        if (event.key === 'Escape') { close(); input.focus(); }
        const buttons = [...results.querySelectorAll('button')];
        if (buttons.length && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            const index = buttons.indexOf(document.activeElement);
            buttons[(index + (event.key === 'ArrowDown' ? 1 : buttons.length - 1)) % buttons.length].focus();
        }
    });
    document.addEventListener('click', event => { if (!wrap.contains(event.target) && !results.contains(event.target)) close(); });
    wrap.addEventListener('focusout', event => { if (!wrap.contains(event.relatedTarget) && !results.contains(event.relatedTarget)) close(); });
    return { close };
}

export function initListingAddressSearch(options) {
    return initListingAutocomplete({
        params: query => ({ q: query, limit: 10, types: 'address,street,place,locality' }),
        label: addressLabel,
        meta: addressMeta,
        filter: item => item.lat != null && item.lng != null && item.lat !== '' && item.lng !== '' && Number.isFinite(Number(item.lat)) && Number.isFinite(Number(item.lng)),
        ...options,
    });
}
